import { useFormContext, useController } from 'react-hook-form';
import { forwardRef, useEffect, useRef } from 'react';
import { cx } from '@emotion/css';

import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';
import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';

import thousandSeparator from '@utils/number/thousand-separator';
import InputBase from '../input/InputBase';

import { CurrencyInputProps } from './types';
import { currencyInputCx } from './styles';
import useCurrencyInputHandler from './usecase/use-currency-input-handler';

const CurrencyInput = forwardRef<HTMLDivElement, CurrencyInputProps>(
  (props, ref) => {
    const {
      name,
      className,
      onKeyDown,
      onChange,
      onBlur,
      rules,
      defaultValue,
      required,
      value,
      isAllowOnlyZero = false,
      ...resProps
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);

    const { control } = useFormContext();
    const {
      fieldState: { error, invalid },
      field: {
        ref: controlRef,
        onChange: controllerOnChange,
        onBlur: controllerOnBlur,
        value: controllerValue,
      },
    } = useController({
      name,
      control,
      defaultValue,
      rules: {
        ...(required && { required: 'This field is required' }),
        ...(rules && rules),
      },
    });

    const prevControllerValue = useRef<string>();

    const { handleKeyDown, handleOnChange } = useCurrencyInputHandler({
      controllerOnChange,
      controllerValue,
      isAllowOnlyZero,
    });

    useEffect(() => {
      const input = inputRef.current;

      if (!input || !controllerValue) return;

      if (
        !prevControllerValue.current &&
        controllerValue !== prevControllerValue.current
      ) {
        input.setAttribute('plain-value', controllerValue);
        input.value = thousandSeparator(controllerValue, {
          isReturnEmptyStringOnEmptyValue: true,
        });

        prevControllerValue.current = controllerValue;
      }

      return () => {
        prevControllerValue.current = '';
      };
    }, [controllerValue, prevControllerValue]);

    return (
      <InputBase
        {...resProps}
        ref={ref}
        className={cx(currencyInputCx, className)}
        onKeyDown={callAllFn(handleKeyDown, onKeyDown)}
        onChange={callAllFn(handleOnChange, onChange)}
        onBlur={callAllFn(controllerOnBlur, onBlur)}
        inputRef={mergeRefs(controlRef, inputRef)}
        error={error}
        isInvalid={invalid}
        value={value}
        InputProps={{
          startAdornment: (
            <div className='currency-symbol'>
              <span>Rp</span>
            </div>
          ),
        }}
        inputProps={{
          maxLength: 10,
        }}
        name={name}
      />
    );
  },
);

export default CurrencyInput;
